<script setup lang="ts">
import { computed } from 'vue'

export type GiftAidChoiceContext = 'single' | 'recurring'

const model = defineModel<boolean | null>({ required: true })

const props = withDefaults(
  defineProps<{
    inputName?: string
    yesInputId?: string
    noInputId?: string
    disabled?: boolean
    error?: string
    /** Single donation vs recording a recurring schedule. */
    context?: GiftAidChoiceContext
    extraNote?: string
  }>(),
  {
    inputName: 'donation-gift-aid-declared',
    yesInputId: 'donation-gift-aid-yes',
    noInputId: 'donation-gift-aid-no',
    disabled: false,
    error: '',
    context: 'single',
    extraNote: '',
  }
)

const questionKey = computed(() =>
  props.context === 'recurring'
    ? 'Dashboard.Giving.AddDonationModal.GiftAidChoice.RecurringQuestion'
    : 'Dashboard.Giving.AddDonationModal.GiftAidChoice.Question'
)

const giftAidErrorId = `${props.inputName}-error`
const giftAidQuestionId = `${props.inputName}-question`
const giftAidExtraNoteId = `${props.inputName}-extra-note`

const describedBy = computed(() => {
  const ids = [
    giftAidQuestionId,
    props.error ? giftAidErrorId : '',
    props.extraNote ? giftAidExtraNoteId : '',
  ].filter(Boolean)
  return ids.join(' ') || undefined
})
</script>

<template>
  <fieldset
    class="sa-gift-aid-choice"
    :class="{ 'sa-gift-aid-choice--invalid': Boolean(error) }"
    :disabled="disabled"
    :aria-invalid="Boolean(error) || undefined"
    :aria-describedby="describedBy"
  >
    <legend class="sa-gift-aid-choice__legend">
      {{ $t('Dashboard.Giving.AddDonationModal.GiftAidChoice.Legend') }}
    </legend>
    <p :id="giftAidQuestionId" class="sa-gift-aid-choice__question">
      {{ $t(questionKey) }}
    </p>

    <div class="sa-gift-aid-choice__options">
      <label
        class="sa-gift-aid-choice__option"
        :class="{ 'sa-gift-aid-choice__option--selected': model === true }"
      >
        <input
          :id="yesInputId"
          v-model="model"
          class="sa-sr-only"
          type="radio"
          :name="inputName"
          :value="true"
          :disabled="disabled"
        />
        <span class="sa-gift-aid-choice__icon sa-gift-aid-choice__icon--yes" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
        <span class="sa-gift-aid-choice__copy">
          <span class="sa-gift-aid-choice__title">
            {{ $t('Dashboard.Giving.AddDonationModal.GiftAidChoice.YesOption') }}
          </span>
        </span>
      </label>

      <label
        class="sa-gift-aid-choice__option"
        :class="{ 'sa-gift-aid-choice__option--selected': model === false }"
      >
        <input
          :id="noInputId"
          v-model="model"
          class="sa-sr-only"
          type="radio"
          :name="inputName"
          :value="false"
          :disabled="disabled"
        />
        <span class="sa-gift-aid-choice__icon sa-gift-aid-choice__icon--no" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
        <span class="sa-gift-aid-choice__copy">
          <span class="sa-gift-aid-choice__title">
            {{ $t('Dashboard.Giving.AddDonationModal.GiftAidChoice.NoOption') }}
          </span>
        </span>
      </label>
    </div>

    <p v-if="error" :id="giftAidErrorId" class="sa-field-error" role="alert">
      {{ error }}
    </p>

    <p class="sa-gift-aid-choice__note">
      {{ $t('Dashboard.Giving.AddDonationModal.GiftAidChoice.Note') }}
    </p>
    <p v-if="extraNote" :id="giftAidExtraNoteId" class="sa-gift-aid-choice__note">
      {{ extraNote }}
    </p>
  </fieldset>
</template>
